import { useMemo } from "react";
import { useCheckInInformation as useCoreCheckInInformation } from "@b2bportal/core-hotels";
import { type CheckInTime, CheckInTimeEnum } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { formatLocalizedTime } from "@overrides/utilities";
import type { CheckInInformationSectionComponentProps } from "@tenant/hotels";
import type { TFunction } from "i18next";

const checkInStr = (
  checkInTime: CheckInTime | undefined,
  t: TFunction,
  language: string
) => {
  if (!checkInTime) {
    return null;
  }

  return checkInTime?.CheckInTime === CheckInTimeEnum.Anytime
    ? t("core-hotels.shop.checkInInformation.checkinAnytime")
    : checkInTime?.checkInTimeUntil
      ? t("core-hotels.shop.checkInInformation.checkinTimeRange", {
          start: formatLocalizedTime(checkInTime?.checkInTimeFrom, language),
          end: formatLocalizedTime(checkInTime?.checkInTimeUntil, language),
        })
      : t("core-hotels.shop.checkInInformation.checkinTime", {
          time: formatLocalizedTime(checkInTime?.checkInTimeFrom, language),
        });
};

export const useCheckInInformation = ({
  checkInInformation,
}: CheckInInformationSectionComponentProps) => {
  const { t, language } = useI18nContext();

  const { context: coreContext, text: coreText } = useCoreCheckInInformation({
    checkInInformation,
  });

  return useMemo(() => {
    return {
      context: coreContext,
      text: {
        ...coreText,
        checkInTime: checkInStr(checkInInformation?.checkInTime, t, language),
        checkOutTime: t("core-hotels.shop.checkInInformation.checkoutTime", {
          time: formatLocalizedTime(checkInInformation?.checkOutTime, language),
        }),
      },
    };
  }, [
    checkInInformation?.checkInTime,
    checkInInformation?.checkOutTime,
    coreContext,
    coreText,
    language,
    t,
  ]);
};
